import { useNavigate } from '@tanstack/react-router';
import { Pause, Play, SlidersHorizontal, Zap } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmPopover } from '~/components/common/confirm-popover';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useScheduleLifecycle } from '~/features/catalog-operations/hooks/use-catalog-ops-schedules';
import type { ScheduleSummary } from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import {
  schedulePlatformOf,
  scheduleWizardRoute,
} from '~/features/catalog-operations/utils/schedule-run-config';
import { cn } from '~/utils/cn';
import { formatInZone, getLocalTimeZone, getZoneOffsetLabel } from '~/utils/date';

import { ScheduleRunTrail } from './schedule-run-trail';
import { ScheduleStatusChip } from './schedule-status-chip';

interface Props {
  schedules: ScheduleSummary[];
  isLoading: boolean;
  /** True when filters are narrowing an otherwise non-empty list — changes the
   *  empty copy from "nothing scheduled" to "nothing matches". */
  isFiltered: boolean;
  /** Row to highlight — the target of a `?scheduleId=` deep link. */
  highlightId?: string | null | undefined;
}

/** Next-run cell. Three states that must read differently: paused (no next run
 *  by definition), Temporal unreachable (`nextRunAt` null on an active
 *  schedule), and an actual upcoming time.
 *
 *  Rendered in the VIEWER's zone, labelled in the column header. The list DTO
 *  carries `cadenceLabel` but not the schedule's `timezone` (only the detail
 *  response has `spec.timezone`), so showing an unlabelled time here would be
 *  exactly the ambiguity the contract warns about. */
function NextRunCell({ schedule, viewerZone }: { schedule: ScheduleSummary; viewerZone: string }) {
  const { t } = useTranslation('catalog-operations');

  if (schedule.status === 'PAUSED') {
    return (
      <span data-testid={`schedules-row-next-run-${schedule.id}`} className="text-muted-foreground">
        {t('schedules.status.paused')}
      </span>
    );
  }
  if (!schedule.nextRunAt) {
    return (
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span
            data-testid={`schedules-row-next-run-${schedule.id}`}
            className="text-muted-foreground"
          >
            {t('schedules.list.liveUnavailable')}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-64 text-center">
          {t('schedules.list.liveUnavailableHint')}
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <span data-testid={`schedules-row-next-run-${schedule.id}`} className="tabular-nums">
      {formatInZone(schedule.nextRunAt, viewerZone)}
    </span>
  );
}

/** 30-day rollup cell. `null` success rate means no runs in the window, which
 *  renders as an em dash — 0% would claim every run failed. */
function ThirtyDayCell({ schedule }: { schedule: ScheduleSummary }) {
  const { t } = useTranslation('catalog-operations');

  if (schedule.successRate === null) {
    return (
      <span
        data-testid={`schedules-row-success-rate-${schedule.id}`}
        className="text-muted-foreground"
      >
        —
      </span>
    );
  }
  return (
    <div>
      <p
        data-testid={`schedules-row-success-rate-${schedule.id}`}
        className={schedule.health === 'FAILING' ? 'text-error font-medium' : 'font-medium'}
      >
        {Math.round(schedule.successRate * 100)}%
      </p>
      <p className="text-muted-foreground text-xs">
        {t('schedules.list.runsAndProducts', {
          runs: schedule.runCount.toLocaleString(),
          products: schedule.productsEnriched.toLocaleString(),
        })}
      </p>
    </div>
  );
}

/** Per-row lifecycle controls. Enabled states follow the API: pause/resume are
 *  mutually exclusive on `status`, and "Run now" is blocked while a run is in
 *  flight because the trigger call bypasses the overlap policy and 409s. */
function RowActions({ schedule }: { schedule: ScheduleSummary }) {
  const { t } = useTranslation('catalog-operations');
  const navigate = useNavigate();
  const { pause, resume, runNow, remove } = useScheduleLifecycle();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isActive = schedule.status === 'ACTIVE';
  const isBusy = pause.isPending || resume.isPending || runNow.isPending || remove.isPending;

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      <Button
        data-testid={`schedules-row-${isActive ? 'pause' : 'resume'}-button-${schedule.id}`}
        variant="outline"
        size="sm"
        className="cursor-pointer"
        isLoading={isActive ? pause.isPending : resume.isPending}
        disabled={isBusy}
        onClick={() => {
          if (isActive) pause.mutate(schedule.id);
          else resume.mutate(schedule.id);
        }}
      >
        {isActive ? <Pause /> : <Play />}
        {isActive ? t('schedules.actions.pause') : t('schedules.actions.resume')}
      </Button>

      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {/* Wrapped in a span so the tooltip still fires on the disabled button. */}
          <span>
            <Button
              data-testid={`schedules-row-run-now-button-${schedule.id}`}
              variant="outline"
              size="sm"
              className="cursor-pointer disabled:cursor-not-allowed"
              isLoading={runNow.isPending}
              disabled={isBusy || schedule.hasRunInFlight}
              onClick={() => {
                runNow.mutate(schedule.id);
              }}
            >
              <Zap />
              {t('schedules.actions.runNow')}
            </Button>
          </span>
        </TooltipTrigger>
        {schedule.hasRunInFlight && (
          <TooltipContent side="top" className="max-w-56 text-center">
            {t('schedules.actions.runNowBlocked')}
          </TooltipContent>
        )}
      </Tooltip>

      {/* Everything about the schedule (what each run does, and when): the batch wizard, filled
          from the schedule, saving only what changed. */}
      <Button
        data-testid={`schedules-row-edit-settings-button-${schedule.id}`}
        variant="ghost"
        size="sm"
        className="cursor-pointer"
        disabled={isBusy}
        onClick={() => {
          void navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: scheduleWizardRoute(schedulePlatformOf(schedule.connector)) as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            search: { scheduleId: schedule.id } as any,
          });
        }}
      >
        <SlidersHorizontal />
        {t('schedules.actions.editSettings')}
      </Button>

      <ConfirmPopover
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        // Soft delete: the schedule is paused, its Temporal handle removed and
        // the row marked DELETED — history survives. Hence "stop and archive".
        title={t('schedules.actions.archiveTitle')}
        description={t('schedules.actions.archiveBody', { name: schedule.name })}
        confirmLabel={t('schedules.actions.archiveConfirm')}
        isPending={remove.isPending}
        testId={`schedules-row-archive-confirm-${schedule.id}`}
        onConfirm={() => {
          remove.mutate(schedule.id, {
            onSuccess: () => {
              setConfirmOpen(false);
            },
          });
        }}
        align="end"
        trigger={
          <Button
            data-testid={`schedules-row-archive-button-${schedule.id}`}
            variant="ghost"
            size="sm"
            className="text-error hover:bg-error-50 hover:text-error cursor-pointer"
            disabled={isBusy}
          >
            {t('schedules.actions.archive')}
          </Button>
        }
      />
    </div>
  );
}

/** The schedules list. One row per schedule, with the run trail and the
 *  lifecycle controls in their real enabled/disabled states. */
export function SchedulesTable({ schedules, isLoading, isFiltered, highlightId }: Props) {
  const { t } = useTranslation('catalog-operations');
  const viewerZone = getLocalTimeZone();

  if (isLoading) {
    return (
      <div data-testid="schedules-table-loading" className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (schedules.length === 0) {
    return (
      <div
        data-testid="schedules-table-empty"
        className="text-muted-foreground rounded-xl border px-5 py-8 text-center text-sm"
      >
        {isFiltered ? t('schedules.list.noMatches') : t('schedules.list.empty')}
      </div>
    );
  }

  return (
    <div className="bg-background overflow-x-auto rounded-xl border">
      <Table data-testid="schedules-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t('schedules.list.columns.schedule')}</TableHead>
            <TableHead>{t('schedules.list.columns.cadence')}</TableHead>
            <TableHead>
              {t('schedules.list.columns.nextRun')}
              {/* The zone is labelled once here rather than on every row: the
                  list DTO has no per-schedule timezone, so all times are the
                  viewer's. */}
              <span className="text-muted-foreground ml-1 normal-case">
                ({getZoneOffsetLabel(viewerZone)})
              </span>
            </TableHead>
            <TableHead>{t('schedules.list.columns.recentRuns')}</TableHead>
            <TableHead>{t('schedules.list.columns.thirtyDays')}</TableHead>
            <TableHead>{t('schedules.list.columns.status')}</TableHead>
            <TableHead className="text-right">{t('schedules.list.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {schedules.map((schedule) => (
            <TableRow
              key={schedule.id}
              data-testid={`schedules-table-row-${schedule.id}`}
              data-state={schedule.id === highlightId ? 'selected' : undefined}
              className={cn(schedule.id === highlightId && 'bg-selected-row hover:bg-selected-row')}
            >
              <TableCell className="font-medium">
                <p data-testid={`schedules-row-name-${schedule.id}`}>{schedule.name}</p>
                <p className="text-muted-foreground text-xs">
                  {t(`schedules.connector.${schedule.connector}`, {
                    defaultValue: schedule.connector,
                  })}{' '}
                  · {schedule.useCase}
                </p>
              </TableCell>
              <TableCell>
                {/* Server-formatted label — never re-derived on the client. */}
                <p data-testid={`schedules-row-cadence-${schedule.id}`}>{schedule.cadenceLabel}</p>
              </TableCell>
              <TableCell>
                <NextRunCell schedule={schedule} viewerZone={viewerZone} />
              </TableCell>
              <TableCell>
                <ScheduleRunTrail runs={schedule.recentRuns} />
              </TableCell>
              <TableCell className="tabular-nums">
                <ThirtyDayCell schedule={schedule} />
              </TableCell>
              <TableCell>
                <ScheduleStatusChip schedule={schedule} />
              </TableCell>
              <TableCell>
                <RowActions schedule={schedule} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
